import {DvTaskData, getWSConnectionStatus, TaskWidgetData, useAppSelector} from '@dv5/domain'
import type {ErrorProcessor} from '@dv5/api'
import {viewsApi} from '@dv5/api'
import {FC, useEffect, useState} from 'react'
import {useWidgetEditor} from '../../hooks/useWidgetEditor'
import {OnNavigateToTask, WidgetProps} from '../../model/WidgetProps'
import {widgetService} from '../../services/widgetService'
import {WidgetFrame} from '../common/WidgetFrame'
import {TaskWidgetToolbar} from './TaskWidgetToolbar'
import './TaskWidget.less'

type Props = WidgetProps & { onNavigateToTask: OnNavigateToTask }

const silentErrorProcessor: ErrorProcessor = {
    displayStep: () => {
    }, displayError: () => {
    }
}

export const TaskWidget: FC<Props> = ({
                                          canvasId,
                                          widget,
                                          selected,
                                          settings,
                                          editingWidgetId,
                                          onStartDrag,
                                          onStartResize,
                                          onContextMenu,
                                          dragState,
                                          onStartLinkCreation,
                                          onCompleteLinkCreation,
                                          onNavigate,
                                          onDeleteDataLink,
                                          onNavigateToTask,
                                      }) => {
    const initialData = widget.data as TaskWidgetData
    const connectionStatus = useAppSelector(getWSConnectionStatus)
    const targetKey = `${initialData.viewId}:${initialData.taskId}`
    const [loaded, setLoaded] = useState<{key: string, data: TaskWidgetData | null, available: boolean} | null>(null)
    useEffect(() => {
        if (widget.targetAvailable === false) return
        let active = true
        viewsApi.getViewItem(initialData.viewId, initialData.taskId, silentErrorProcessor, item => {
            if (!active) return
            if (item?.data?.type === 'TASK') {
                const taskData = item.data as DvTaskData
                setLoaded({key: targetKey, available: true, data: {
                    ...initialData, title: item.title, code: taskData.code, status: taskData.status,
                    description: taskData.description, closed: taskData.closed, estimation: taskData.estimation
                }})
            } else {
                setLoaded({key: targetKey, available: false, data: null})
            }
        }).catch(() => {
            if (active) setLoaded({key: targetKey, available: false, data: null})
        })
        return () => {
            active = false
        }
    }, [initialData.taskId, initialData.viewId, widget.targetAvailable, connectionStatus])
    const currentLoaded = loaded?.key === targetKey ? loaded : null
    const broken = widget.targetAvailable === false || currentLoaded?.available === false
    const data = currentLoaded?.available && currentLoaded.data ? currentLoaded.data : initialData
    const {containerRef, resizeCursor, handlers, renderResizeHandles, renderLinkDots} = useWidgetEditor({
        widget, selected, editingWidgetId, dragState, onStartDrag, onStartResize, onContextMenu,
        onStartLinkCreation, onCompleteLinkCreation, onNavigate, onDeleteDataLink,
    })
    // Visibility flags must come from widget.data (store), which is updated in real time via WS;
    // the local `data` state only refreshes on task fetch and would hold stale flags after a remote toggle.
    const toolbarData = {
        ...data,
        hideEstimation: initialData.hideEstimation,
        hideDescription: initialData.hideDescription,
    }
    return <>
        {selected && <TaskWidgetToolbar
            canvasId={canvasId}
            widgetId={widget.id}
            data={toolbarData}
            readOnly={settings.readOnly}
            targetAvailable={!broken}
            toolbarStyles={widgetService.getCenteredToolbarStyle(widget)}
            onNavigateToTask={onNavigateToTask}
        />}
        <WidgetFrame
            baseClass='taskWidget'
            canvasId={canvasId}
            widget={widget}
            settings={settings}
            selected={selected}
            editingWidgetId={editingWidgetId}
            resizeCursor={resizeCursor}
            handlers={handlers}
            containerRef={containerRef}
            renderResizeHandles={renderResizeHandles}
            renderLinkDots={renderLinkDots}
            onNavigate={onNavigate}
            onDeleteDataLink={onDeleteDataLink}
            showTypeIconOnHover
        >
            <div className="taskWidget-content">
                {broken ? <>
                    <strong>Task</strong>
                    <h3>{initialData.title || 'Task'}</h3>
                    <p className="taskWidget-brokenLink" role="status">Broken link</p>
                </> : <>
                <div className="taskWidget-heading"><strong>{data.code || 'Task'}</strong>{data.status &&
                    <span>{data.status}</span>}</div>
                <h3>{data.title || 'Task unavailable'}</h3>
                {(data.closed || (data.estimation !== undefined && !initialData.hideEstimation)) &&
                    <div className="taskWidget-details">
                        {data.closed && <span>Closed</span>}
                        {data.estimation !== undefined && !initialData.hideEstimation &&
                            <span>Estimate: {data.estimation}</span>}
                    </div>}
                {!initialData.hideDescription && data.description && <p>{data.description}</p>}
                </>}
            </div>
        </WidgetFrame>
    </>
}
